import React, {useEffect, useReducer, useCallback} from 'react';
import {useAppSelector, useAppDispatch} from '../../utils/hooks';
import {
  weightSchema,
  pregnancySchema,
  deceasedSchema,
  heightSchema,
  treatmentSchema,
  traitSchema,
  moveSchema,
  uniqueIds,
  warakirriPregancySchema,
  warakirriMoveSchema,
  warakirriSchema,
  warakirriStatusSchema,
  warakirriBirthDateSchema,
  digirodeoAddUHF,
  warakirriRfidReplacementSchema,
  warakirriVisualTagReplacementSchema,
} from '../../config/schema';
import {flattenTree} from '../../store/site/actions';
import {LayerNode} from '../../store/site/types';
import {fetchSiteTokens, fetchCattleTreatment} from '../../store/site/actions';
import CONSTANT from '../../config/constant';
import {validateWithSchema} from '../../utils/csvReader';
import {cleanSelectedFile} from './Components';
import {manageAssetByCSV} from '../../store/asset/actions';
import {Buttons} from '../../presentation/ButtonsGroup';
import {toggleModal, toggleModalOff} from '../../store/modal/actions';

type DropDownType = {
  title: string;
  value: string;
  onChange: (e: React.ChangeEvent<{name?: string; value: unknown}>) => void;
  list: string[];
  ifUpperCase: boolean;
  ifDisplay: boolean;
  hasSearch?: boolean; //if dropdown searchable
  required?: true,
};
interface ManageAssetDataType {
  label: string;
  index: number;
  schema: any;
  value: string;
  updateType: string;
  dropDown?: Array<DropDownType>;
  dropDownValue?: {[key: string]: string};
}
type accountType = 'warakirri' | 'normal';

const INITIAL_MANAGE_DATA = {
  index: '' as string | number,
  file: null as any,
  csvData: [] as any[],
  validationResult: {
    type: null,
    validationError: [],
  },
  open: false,
  treatmentDropdown: {
    medication_name: '',
    dose_unit: '',
  },
  warakirriPregnancyDropdown: {
    conception_status: '',
    conception_method: '',
  },
  warakirriMoveDropdown: {
    site_moved_to: '',
    site_moved_to_region: '',
    site_moved_to_property_type: '',
    pic_id: '',
    pic_name: '',
  },
  warakirriStatusDropdown: {
    status: '',
  },
  medicationNameList: [],
};

type Action =
  | {type: 'change/index'; index: number}
  | {type: 'change/csv'; csvData: any[]}
  | {
      type: 'change/validationResult';
      validationResult: typeof INITIAL_MANAGE_DATA.validationResult;
    }
  | {type: 'change/open'; open: boolean}
  | {type: 'upload/file'; file: any}
  | {
      type: 'click/closeAssetDialog';
      file: any;
      csvData: any[];
      validationResult: typeof INITIAL_MANAGE_DATA.validationResult;
      open: boolean;
    }
  | {
      type: 'click/rightButtonClickIsErr';
      file: any;
      validationResult: typeof INITIAL_MANAGE_DATA.validationResult;
      open: boolean;
    }
  | {
      type: 'select/warakirriPregnancyDropdown';
      warakirriPregnancyDropdown: typeof INITIAL_MANAGE_DATA.warakirriPregnancyDropdown;
    }
  | {
      type: 'select/warakirriMoveDropdown';
      warakirriMoveDropdown: typeof INITIAL_MANAGE_DATA.warakirriMoveDropdown;
    }
  | {
      type: 'select/warakirriStatusDropdown';
      warakirriStatusDropdown: typeof INITIAL_MANAGE_DATA.warakirriStatusDropdown;
    }
  | {
      type: 'select/treatmentDropdown';
      treatmentDropdown: typeof INITIAL_MANAGE_DATA.treatmentDropdown;
    }
  | {
      type: 'fetch/medicationName';
      medicationNameList: {label: string;}[];
    };
const reducer = (
  prevState: typeof INITIAL_MANAGE_DATA,
  action: Action,
): typeof INITIAL_MANAGE_DATA => {
  const {type, ...actionData} = action;
  switch (action.type) {
    case 'change/index':
    case 'change/csv':
    case 'change/validationResult':
    case 'change/open':
    case 'select/warakirriPregnancyDropdown':
    case 'select/warakirriMoveDropdown':
    case 'select/warakirriStatusDropdown':
    case 'select/treatmentDropdown':
    case 'click/closeAssetDialog':
    case 'click/rightButtonClickIsErr':
    case 'upload/file':
    case 'fetch/medicationName':
      return {...prevState, ...actionData};
  }
};
export const ManageAssetByCSVObject = (account: accountType) => {
  const dispatch = useAppDispatch();
  const [state, localDispatch] = useReducer(reducer, {...INITIAL_MANAGE_DATA});
  const siteTokens = useAppSelector((state) => state.site)?.sort(
    (token, token2) => {
      return token.details.siteName > token2.details.siteName ? 1 : -1;
    },
  );
  const locationState = useAppSelector((state) => state.location);
  const hasDigiRodeo = useAppSelector((state) => state.user.plugins).find(
    (plg) => plg.name === 'DigiRodeo',
  );

  const updateNormalForm = (
    e: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    localDispatch({type: 'change/index', index: Number(e.target.value)});
  };

  const uniqueIdTypes = uniqueIds.Australia;
  /**warakirri */
  const conceptionStatusList = ['EMPTY', 'JOINED', 'PTIC'];
  const conceptionMethodList = ['CONVENTIONAL', 'BULL', 'SEXED'];
  const siteMovedToList: Array<string> = [];
  const statusList = ['Dead', 'Sold', 'Exported'];
  const doseUnitList = ['ML', 'G', 'Unit'];
  /**flat all pic sites and save pic and site relation map*/
  const siteStates: Array<{
    layers: LayerNode;
    location: string;
    region?: string;
    propertyType?: string;
  }> = siteTokens.map((token) => {
    const {layers, location, region, propertyType} = token.details;
    return {
      layers,
      location,
      region,
      propertyType,
    };
  });
  const siteDetailsMap = new Map<
    string,
    {location: string; region?: string; propertyType?: string}
  >();
  if (siteStates.length > 0) {
    siteStates.map((siteState) => {
      siteMovedToList.push(siteState.layers.name);
      siteDetailsMap.set(siteState.layers.name, {...siteState});
    });
  }
  const updateSharedForm = useCallback(
    (type: string, fieldName: string) => (
      e?:
        | React.ChangeEvent<{
            name?: string;
            value?: unknown;
          }>
        | any, //treatment Autocompelete return {lable:string, number: string}
    ) => {
      switch (type) {
        case 'Treatment':
          const treatmentDropdown =
            state.treatmentDropdown;
          if (fieldName === 'medication_name') {
            localDispatch({
              type: 'select/treatmentDropdown',
              treatmentDropdown: {
                ...treatmentDropdown,
                medication_name: e?.label,
              },
            });
          } else {
            localDispatch({
              type: 'select/treatmentDropdown',
              treatmentDropdown: {
                ...treatmentDropdown,
                [fieldName]: e.target.value,
              },
            });
          }
          break;
      }
    }, [state.treatmentDropdown])
  const updateWarakirriForm = useCallback(
    (type: string, fieldName: string) => (
      e?:
        | React.ChangeEvent<{
            name?: string;
            value?: unknown;
          }>
        | any, //treatment Autocompelete return {lable:string, number: string}
    ) => {
      switch (type) {
        case 'Pregancy':
          const otherWarakirriPregnancyDropdown =
            state.warakirriPregnancyDropdown;
          localDispatch({
            type: 'select/warakirriPregnancyDropdown',
            warakirriPregnancyDropdown: {
              ...otherWarakirriPregnancyDropdown,
              [fieldName]: e.target.value,
            },
          });
          break;
        case 'Move':
          localDispatch({
            type: 'select/warakirriMoveDropdown',
            warakirriMoveDropdown: {
              site_moved_to: String(e.target.value),
              site_moved_to_region:
                siteDetailsMap.get(e.target.value)?.region ?? '',
              site_moved_to_property_type:
                siteDetailsMap.get(e.target.value)?.propertyType ?? '',
              pic_id: siteDetailsMap.get(e.target.value)?.location,
              pic_name:
                Object.values(locationState.location)?.find(
                  (item) =>
                    item?.PICAddress ===
                    siteDetailsMap.get(e.target.value)?.location,
                )?.locationNickname ?? '',
            },
          });
          break;
        case 'Status':
          localDispatch({
            type: 'select/warakirriStatusDropdown',
            warakirriStatusDropdown: {status: String(e.target.value)},
          });
          break;
      }
    },
    [
      state.warakirriPregnancyDropdown,
      siteDetailsMap,
      locationState.location,
    ],
  );

  const sharedDropDownList: {[key: string]: Array<DropDownType>} = {
    Treatment: [
      {
        title: 'Medication Name',
        value: state.treatmentDropdown.medication_name,
        onChange: updateSharedForm('Treatment', 'medication_name'),
        list: state.medicationNameList,
        ifUpperCase: false,
        ifDisplay: true,
        hasSearch: true,
        required: true,
      },
      {
        title: 'Dose Unit',
        value: state.treatmentDropdown.dose_unit,
        onChange: updateSharedForm('Treatment', 'dose_unit'),
        list: doseUnitList,
        ifUpperCase: false,
        ifDisplay: true,
        required: true,
      },
    ],
  }

  const warakirriDropDownList: {[key: string]: Array<DropDownType>} = {
    Pregancy: [
      {
        title: 'Conception Status',
        value: state.warakirriPregnancyDropdown.conception_status,
        onChange: updateWarakirriForm('Pregancy', 'conception_status'),
        list: conceptionStatusList,
        ifUpperCase: true,
        ifDisplay: false,
      },
      {
        title: 'Conception Method',
        value: state.warakirriPregnancyDropdown.conception_method,
        onChange: updateWarakirriForm('Pregancy', 'conception_method'),
        list: conceptionMethodList,
        ifUpperCase: true,
        ifDisplay: false,
      },
    ],
    Move: [
      {
        title: 'Site Moved to',
        value: state.warakirriMoveDropdown.site_moved_to,
        onChange: updateWarakirriForm('Move', ''),
        list: siteMovedToList,
        ifUpperCase: true,
        ifDisplay: true,
      },
    ],
    Status: [
      {
        title: 'Status',
        value: state.warakirriStatusDropdown.status,
        onChange: updateWarakirriForm('Status', ''),
        list: statusList,
        ifUpperCase: false,
        ifDisplay: false,
      },
    ],
  };
  const normalData: Array<ManageAssetDataType> = [
    {
      label: 'Manage - Weight - Template',
      index: 0,
      schema: weightSchema,
      value: CONSTANT.TEMPLATE.MANAGEMENT.WEIGHT.VALUE,
      updateType: 'updateWeight',
    },
    {
      label: 'Manage - Pregnancy - Template',
      index: 1,
      schema: pregnancySchema,
      value: CONSTANT.TEMPLATE.MANAGEMENT.PREGNANCY.VALUE,
      updateType: 'updatePregnancyStatus',
    },
    {
      label: 'Manage - Deceased - Template',
      index: 2,
      schema: deceasedSchema,
      value: CONSTANT.TEMPLATE.MANAGEMENT.DECEASED.VALUE,
      updateType: 'updateDeceaseStatus',
    },
    {
      label: 'Manage - Height - Template',
      index: 3,
      schema: heightSchema,
      value: CONSTANT.TEMPLATE.MANAGEMENT.HEIGHT.VALUE,
      updateType: 'updateHeight',
    },
    {
      label: 'Manage - Treatment - Template',
      index: 4,
      schema: treatmentSchema,
      value: CONSTANT.TEMPLATE.MANAGEMENT.TREATMENT.VALUE,
      updateType: 'addTreatment',
      dropDown: sharedDropDownList.Treatment,
      dropDownValue: state.treatmentDropdown,
    },
    {
      label: 'Manage - Traits - Template',
      index: 5,
      schema: traitSchema,
      value: CONSTANT.TEMPLATE.MANAGEMENT.TRAITS.VALUE,
      updateType: 'addTraits',
    },
  ];
  if (hasDigiRodeo && hasDigiRodeo.status === 'activated') {
    normalData.push({
      label: 'Manage - DigiRodeo - Template',
      index: 6,
      schema: digirodeoAddUHF,
      value: CONSTANT.TEMPLATE.MANAGEMENT.ATTACHMENT.VALUE,
      updateType: 'add_tag',
    });
  }

  const warakirriData: Array<ManageAssetDataType> = [
    {
      label: 'Manage - Weight - Template',
      index: 0,
      schema: warakirriSchema,
      value: CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.WEIGHT.VALUE,
      updateType: 'updateWeight',
    },
    {
      label: 'Manage - Pregnancy - Template',
      index: 1,
      schema: warakirriPregancySchema,
      value: CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.PREGNANCY.VALUE,
      updateType: 'updatePregnancyStatus',
      dropDown: warakirriDropDownList.Pregancy,
      dropDownValue: state.warakirriPregnancyDropdown,
    },
    {
      label: 'Manage - Status - Template',
      index: 2,
      schema: warakirriStatusSchema,
      value: CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.STATUS.VALUE,
      updateType: 'updateStatus',
      dropDown: warakirriDropDownList.Status,
      dropDownValue: state.warakirriStatusDropdown,
    },
    {
      label: 'Manage - Treatment - Template',
      index: 3,
      schema: treatmentSchema,
      value: CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.TREATMENT.VALUE,
      updateType: 'addTreatment',
      dropDown: sharedDropDownList.Treatment,
      dropDownValue: state.treatmentDropdown,
    },
    {
      label: 'Manage - Move - Template',
      index: 4,
      schema: warakirriMoveSchema,
      value: CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.MOVE.VALUE,
      updateType: 'addMovement',
      dropDown: warakirriDropDownList.Move,
      dropDownValue: state.warakirriMoveDropdown,
    },
    {
      label: 'Manage - Birth Date - Template',
      index: 5,
      schema: warakirriBirthDateSchema,
      value: CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.BIRTH_DATE.VALUE,
      updateType: 'addBirthDate',
    },
    {
      label: 'Manage - RFID Replacement - Template',
      index: 6,
      schema: warakirriRfidReplacementSchema,
      value: CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.RFID_REPLACEMENT.VALUE,
      updateType: 'replaceRFID',
    },
    {
      label: 'Manage - Visual Tag Replacement - Template',
      index: 7,
      schema: warakirriVisualTagReplacementSchema,
      value:
        CONSTANT.TEMPLATE.WARAKIRRI_MANAGEMENT.VISUAL_TAG_REPLACEMENT.VALUE,
      updateType: 'replaceVisualTag',
    },
  ];
  /**switch dropdown ui */
  const manageData = (): Array<{[key: string]: any}> => {
    switch (account) {
      case 'normal':
        return normalData;
      case 'warakirri':
        return warakirriData; //warakirri delete all dropdown ui
    }
  };
  const dataSelection = manageData()[Number(state.index)];
  const isWarakirri = account === 'warakirri';
  const csvRequestData = () => {
    const csvObj = {
      template: dataSelection.value as any,
      activityType: dataSelection.updateType as any,
      externalIds: uniqueIdTypes.reduce((sum, current) => sum + ',' + current),
      uniqueExternalIds: uniqueIdTypes.reduce(
        (sum, current) => sum + ',' + current,
      ),
      tokenType: CONSTANT.ASSETTYPE.ASSET as any,
      file: state.file,
      csvData: state.csvData,
      schemaType: dataSelection.schema.id,
    };
    if (dataSelection.schema.apiType) {
      csvObj['type'] = dataSelection.schema.apiType;
    }
    if (dataSelection.dropDownValue) {
      /**dropdown value will be csvObj.preset_** =value */
      Object.entries(dataSelection.dropDownValue).forEach(([key, value]: [string, any]) => {
        if (value) {
          csvObj[`preset_${key}`] = value;
        }
      });
    }
    return csvObj;
  };
  const validateWithSchemaFunction = () => {
    validateWithSchema(
      state.csvData,
      dataSelection.schema,
      false,
      uniqueIdTypes,
    )
      .then(() => {
        //reset to empty in case has previous error
        localDispatch({
          type: 'change/validationResult',
          validationResult: {
            type: null,
            validationError: [],
          },
        });
      })
      .catch((e) => {
        localDispatch({
          type: 'change/validationResult',
          validationResult: {
            type: e.type,
            validationError: e.error || [],
          },
        });
      });
    localDispatch({
      type: 'change/open',
      open: true,
    });
  };
  const closeAssetDialog = () => {
    cleanSelectedFile('csvReader');
    localDispatch({
      type: 'click/closeAssetDialog',
      file: '',
      csvData: [],
      validationResult: {type: null, validationError: []},
      open: false,
    });
  };
  const assetDialogRightButton = () => {
    localDispatch({
      type: 'change/open',
      open: false,
    });
    dispatch(manageAssetByCSV(csvRequestData()))
      .then((data) => {
        cleanSelectedFile('csvReader');
        localDispatch({type: 'upload/file', file: ''});
      })
      .catch((error) => {
        cleanSelectedFile('csvReader');
        localDispatch({type: 'upload/file', file: ''});
        const errorList = [];
        if (Array.isArray(error.error) && error.error[0].source?.data?.length) {
          error.error[0].source.data.map(
            (err: {
              row: number;
              errors: Array<string>;
              [key: string]: any; // product_number
            }) => {
              errorList.push({
                row: err.row,
                message:
                  err?.errors?.[0] ||
                  err.error.join('. ') ||
                  error.error[0].details,
              });
            },
          );
        } else {
          errorList.push(error);
        }
        dispatch(
          toggleModal({
            status: 'failed',
            title: 'An error has occurred!',
            errorInfo:
              typeof error.message === 'string'
                ? errorList
                : 'Please try again later.',
            renderButton: (
              <Buttons
                leftButtonTitle="Cancel"
                rightButtonTitle="Upload CSV Again"
                leftButtonOnClick={() => {
                  dispatch(toggleModalOff());
                  cleanSelectedFile('csvReader');
                  localDispatch({type: 'upload/file', file: ''});
                }}
                rightButtonOnClick={() => {
                  dispatch(toggleModalOff());
                  cleanSelectedFile('csvReader');
                  localDispatch({type: 'upload/file', file: ''});
                }}
              />
            ),
          }),
        );
      });
  };
  const assetDialogrightButtonClickIsErr = () => {
    cleanSelectedFile('csvReader');
    localDispatch({
      type: 'click/rightButtonClickIsErr',
      file: '',
      validationResult: {type: null, validationError: []},
      open: false,
    });
  };
  const setFile = (file: any) => {
    localDispatch({
      type: 'upload/file',
      file: file,
    });
  };
  const setCSVData = (csvData: any[]) => {
    localDispatch({
      type: 'change/csv',
      csvData: csvData,
    });
  };
  useEffect(() => {
    dispatch(fetchSiteTokens());
    dispatch(fetchCattleTreatment(isWarakirri)).then((response) => {
      localDispatch({
        type: 'fetch/medicationName',
        medicationNameList: response?.map((item) => {
          return {
            label: item.details.product_name,
          };
        }),
      });
    });
  }, [dispatch, isWarakirri]);
  return {
    state,
    updateWarakirriForm,
    updateNormalForm,
    csvRequestData,
    // disableUploadButton,
    manageData,
    isWarakirri,
    uniqueIdTypes,
    dataSelection,
    validateWithSchemaFunction,
    closeAssetDialog,
    assetDialogRightButton,
    assetDialogrightButtonClickIsErr,
    setFile,
    setCSVData,
  };
};
